import type { GroupMode } from "@listou/shared/constants";
import { stickerUrl } from "@listou/shared/stickers";

/**
 * The two landings for groups: the Secret Santa, and lists gathered behind one link.
 * Wording, SEO and FAQ in one place, like the occasion landings (occasion-pages.ts); the
 * FAQ doubles as the page's FAQPage structured data, so it is written as plain text.
 */
/** A collection is reached from the lists landing: same page, "pour vous seul". */
export type LandingGroupMode = Exclude<GroupMode, "collection">;

export type GroupPage = {
  mode: LandingGroupMode;
  path: "/secret-santa" | "/listes-en-groupe";
  create: "/secret-santa/nouveau" | "/groupes/nouveau";
  /** Short name for links. */
  linkLabel: string;
  kicker: string;
  h1: string;
  subtitle: string;
  cta: string;
  reassurance: string;
  features: { title: string; text: string; sticker: string }[];
  faq: [question: string, answer: string][];
  seoTitle: string;
  seoDescription: string;
  /** 1200x630 link-preview card, made by `apps/server/scripts/make-og-images.ts`. */
  ogImage: string;
  finalCta: string;
};

const s = stickerUrl;

export const GROUP_PAGES: Record<LandingGroupMode, GroupPage> = {
  secret_santa: {
    mode: "secret_santa",
    path: "/secret-santa",
    create: "/secret-santa/nouveau",
    linkLabel: "Secret Santa",
    kicker: "Secret Santa en ligne",
    h1: "Tirez au sort. Chacun sait quoi offrir.",
    subtitle:
      "Vous ajoutez les prénoms, Listou fait le tirage et prévient tout le monde. Et comme chacun a sa liste d'envies, plus personne n'offre une bougie parfumée par défaut.",
    cta: "Organiser mon Secret Santa",
    reassurance: "Gratuit, sans compte pour les participants.",
    features: [
      {
        title: "Un tirage qui respecte les couples",
        text: "Collez la même gommette sur un couple ou une famille : ils ne se tirent pas entre eux. Deux familles peuvent aussi s'exclure.",
        sticker: s("noel", "boule"),
      },
      {
        title: "Par e-mail, ou juste un lien",
        text: "Ceux qui ont une adresse reçoivent leur tirage. Les autres ouvrent le lien du groupe et choisissent leur prénom.",
        sticker: s("noel", "chaussette"),
      },
      {
        title: "La liste de celui qu'on a tiré",
        text: "Le tirage mène directement à ses envies, avec le budget en tête. Fini les cadeaux au hasard.",
        sticker: s("noel", "cadeau"),
      },
      {
        title: "Et l'an prochain, on recommence",
        text: "Mêmes participants, mêmes règles, et personne ne retombe sur la même personne.",
        sticker: s("noel", "sapin"),
      },
    ],
    faq: [
      [
        "Comment organiser un Secret Santa en ligne ?",
        "Créez le groupe, ajoutez les prénoms des participants (et leur e-mail si vous l'avez), puis lancez le tirage. Chacun découvre à qui il offre depuis son e-mail ou depuis la page du groupe, sans créer de compte.",
      ],
      [
        "Comment éviter qu'un couple se tire au sort ?",
        "Collez-leur la même gommette de couleur : les personnes d'une même gommette ne se tirent jamais entre elles. Vous pouvez aussi exclure une personne précise, ou empêcher deux gommettes de se tirer.",
      ],
      [
        "Les participants doivent-ils créer un compte ?",
        "Non. Un e-mail suffit, et même sans e-mail, le lien du groupe suffit : chacun choisit son prénom.",
      ],
      [
        "L'organisateur voit-il le tirage ?",
        "Oui, s'il le souhaite : le résultat reste replié sur sa page, et chaque participant est prévenu que l'organisateur peut le voir.",
      ],
      [
        "Quelqu'un a cliqué sur le mauvais prénom, que faire ?",
        "Il clique sur « Ce n'est pas moi ». S'il avait déjà découvert le tirage, Listou l'échange discrètement avec celui d'une personne qui n'a pas encore regardé : ce qu'il a vu n'est plus vrai.",
      ],
      [
        "Combien faut-il être pour un Secret Santa ?",
        "Au moins trois personnes, et jusqu'à cinquante par groupe. Le tirage fonctionne avec un nombre pair ou impair de participants : chacun offre à une personne et reçoit d'une autre.",
      ],
      [
        "Peut-on faire un Secret Santa à distance ?",
        "Oui : le tirage et les listes sont en ligne. Chacun fait livrer son cadeau directement chez la personne tirée, ou l'apporte le jour de l'échange.",
      ],
      [
        "Et si quelqu'un arrive après le tirage ?",
        "Ajoutez-le au groupe, puis refaites le tirage. Listou vous prévient si des participants avaient déjà découvert le leur.",
      ],
      [
        "Peut-on éviter de retomber sur la même personne que l'an dernier ?",
        "Oui. En préparant l'édition suivante depuis le groupe de l'année passée, Listou garde les participants et leurs règles, et écarte les tirages de l'an dernier.",
      ],
      [
        "Le Secret Santa est-il gratuit ?",
        "Oui. Listou se rémunère par une petite commission quand un cadeau est acheté via un lien vers une boutique, sans changer le prix.",
      ],
    ],
    seoTitle: "Secret Santa en ligne gratuit : tirage au sort du Père Noël secret · Listou",
    seoDescription:
      "Organisez votre Secret Santa (Père Noël secret) en ligne : tirage au sort avec exclusions pour les couples, envoi par e-mail ou par lien, et la liste d'envies de chacun. Gratuit, sans compte.",
    ogImage: "/og/secret-santa.png",
    finalCta: "Le tirage est fait en deux minutes.",
  },
  lists: {
    mode: "lists",
    path: "/listes-en-groupe",
    create: "/groupes/nouveau",
    linkLabel: "Listes en groupe",
    kicker: "Toutes les listes, un seul lien",
    h1: "Un lien dans la conversation, et chacun sa liste.",
    subtitle:
      "Pour Noël en famille, l'anniversaire des jumeaux ou les cadeaux entre collègues : vous envoyez un lien, chacun choisit son prénom et remplit sa liste. Ou pour vous seul : « Déco », avec la chambre et la salle à manger.",
    cta: "Créer un groupe de listes",
    reassurance: "Gratuit, sans compte pour personne.",
    features: [
      {
        title: "Chacun choisit son prénom",
        text: "Pas d'inscription : on clique sur le lien, on dit qui on est, on ajoute ses idées.",
        sticker: s("noel", "bonhomme"),
      },
      {
        title: "Vous remplissez pour Mamie",
        text: "Pour ceux qui ne viendront pas eux-mêmes, la liste est à vous jusqu'à ce qu'ils la réclament.",
        sticker: s("hiver", "mug"),
      },
      {
        title: "La surprise reste gardée",
        text: "Chacun voit ce qui est pris sur les listes des autres. Jamais sur la sienne.",
        sticker: s("noel", "bonnet"),
      },
      {
        title: "Plus de cadeaux en double",
        text: "Un cadeau réservé l'est pour tout le monde : les deux tantes n'offriront pas le même pull.",
        sticker: s("hiver", "pull"),
      },
    ],
    faq: [
      [
        "Comment faire une liste de Noël commune pour toute la famille ?",
        "Créez un groupe de listes et envoyez son lien à la famille. Chacun choisit son prénom et remplit sa propre liste ; tout le monde voit celles des autres et peut réserver un cadeau.",
      ],
      [
        "Est-ce que chacun doit créer un compte ?",
        "Non. Un prénom suffit. Ceux qui veulent retrouver leur liste sur un autre téléphone peuvent ajouter leur e-mail plus tard.",
      ],
      [
        "Est-ce que je vois ce qu'on m'offre ?",
        "Non. Vous voyez ce qui est réservé sur les listes des autres, jamais sur la vôtre. La surprise tient jusqu'au jour J.",
      ],
      [
        "Et pour les enfants, ou Mamie qui n'a pas de téléphone ?",
        "L'organisateur remplit leur liste à leur place. Un parent peut aussi choisir le prénom de son enfant et tenir sa liste.",
      ],
      [
        "Je peux faire un groupe pour moi tout seul ?",
        "Oui : choisissez « Pour moi » en créant le groupe. Vous rangez plusieurs listes sous un même lien, par exemple « Déco » avec « Chambre » et « Salle à manger », et vos proches les parcourent comme une seule.",
      ],
      [
        "Ça marche pour autre chose que Noël ?",
        "Oui : anniversaires d'une bande d'amis, cadeaux entre collègues, naissance de jumeaux. Choisissez l'occasion en créant le groupe.",
      ],
    ],
    seoTitle: "Listes de cadeaux en groupe : famille, amis, collègues · Listou",
    seoDescription:
      "Réunissez les listes d'envies de toute la famille derrière un seul lien. Chacun remplit la sienne sans compte, réserve sur celles des autres, et garde la surprise.",
    ogImage: "/og/listes-en-groupe.png",
    finalCta: "Un lien, et tout le monde a sa liste.",
  },
};

export const groupPageJsonLd = (page: GroupPage) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: page.faq.map(([question, answer]) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});
